import { useSuspenseQuery } from '@tanstack/react-query';
import { Link, Outlet } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { Building2, Users, MapPin, Settings, CreditCard, ArrowLeft } from 'lucide-react';
import { organizationQueries } from '@/features/organizations/queries';
import { PageTabs } from '@/common/components/page-tabs/PageTabs';

export function OrganizationDetailPage({ organizationId }: { organizationId: number }) {
  const { data: org } = useSuspenseQuery(organizationQueries.detail(organizationId));

  return (
    <div className="space-y-6 pb-10">
      <div>
        <Link
          to="/organizations"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4 group"
        >
          <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform" />
          <Trans>All organizations</Trans>
        </Link>

        <div className="flex items-start gap-4">
          <div className="flex size-12 items-center justify-center rounded-2xl bg-primary shrink-0">
            <Building2 size={22} className="text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{org.name}</h1>
            {(org.city || org.street) && (
              <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1">
                <MapPin size={13} />
                {[org.street, org.city].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </div>
      </div>

      <PageTabs>
        <PageTabs.List>
          <PageTabs.Tab
            to="/organizations/$organizationId/supply-points"
            params={{ organizationId }}
            icon={MapPin}
          >
            <Trans>Supply points</Trans>
          </PageTabs.Tab>
          <PageTabs.Tab
            to="/organizations/$organizationId/members"
            params={{ organizationId }}
            icon={Users}
          >
            <Trans>Members</Trans>
          </PageTabs.Tab>
          <PageTabs.Tab
            to="/organizations/$organizationId/billing"
            params={{ organizationId }}
            icon={CreditCard}
          >
            <Trans>Billing</Trans>
          </PageTabs.Tab>
          <PageTabs.Tab
            to="/organizations/$organizationId/settings"
            params={{ organizationId }}
            icon={Settings}
          >
            <Trans>Settings</Trans>
          </PageTabs.Tab>
        </PageTabs.List>
        <Outlet />
      </PageTabs>
    </div>
  );
}
